import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useParent } from '../../contexts/parentContext';
import api from '../../services/api';

// Import separated components
import TeamHeader from '../../components/parent/team/TeamHeader';
import TeamAnalytics from '../../components/parent/team/TeamAnalytics';
import TeamOverview from '../../components/parent/team/TeamOverview';
import TeamPlayers from '../../components/parent/team/TeamPlayers';
import TeamStaff from '../../components/parent/team/TeamStaff';
import TeamAnalyticsTab from '../../components/parent/team/TeamAnalyticsTab';

const ChildControlTeam = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedChild } = useParent();
  const [teamData, setTeamData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (selectedChild) {
      fetchTeamData();
    }
  }, [selectedChild]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!selectedChild?.userId) {
        setError('No selected child or child userId');
        return;
      }

      // Fetch team data by player ID
      const teamResult = await api.get(`/team/show-team-by-player-id?playerId=${selectedChild.userId}`);
      
      if (teamResult.data?.team && teamResult.data?.teamMembers) {
        const team = teamResult.data.team;
        const teamMembers = teamResult.data.teamMembers;
        const statistics = teamResult.data.statistics;
        
        // Set team data
        setTeamData({
          name: team.name || 'Unknown Team',
          logo: team.logo || null,
          coach: 'Team Coach', // Not provided in API, could be derived from supervisors
          league: team.organization?.name || 'Unknown Organization',
          founded: new Date(team.createdAt).getFullYear().toString() || 'Unknown',
          homeGround: 'Home Ground', // Not provided in API
          totalMembers: statistics?.totalMembers || teamMembers.length || 0,
          activePlayers: statistics?.players || teamMembers.filter(m => m.type === 'player').length || 0,
          supervisors: statistics?.supervisors || teamMembers.filter(m => m.type === 'supervisor').length || 0,
          averageAge: 0, // Not provided in API
          wins: 0, // Not provided in API
          losses: 0, // Not provided in API
          draws: 0, // Not provided in API
          goalsFor: 0, // Not provided in API
          goalsAgainst: 0, // Not provided in API
          sport: team.sport || 'Unknown',
          type: team.type || 'Unknown',
          gender: team.gender || 'Unknown',
          uniqueName: team.uniqueName || '',
          organization: team.organization || null
        });

        // Set team members
        if (teamMembers && Array.isArray(teamMembers)) {
          const formattedMembers = teamMembers.map(member => ({
            _id: member.userId,
            firstName: '', // Not provided in API
            lastName: '', // Not provided in API
            userName: member.userName || 'unknown',
            accountType: member.type || member.userType || 'player',
            profilePicture: member.profilePicture || null,
            position: member.title || (member.type === 'supervisor' ? 'Staff' : 'Player'),
            isOnline: false, // Not provided in API
            jerseyNumber: null, // Not provided in API
            joinDate: member.joinedAt || new Date().toISOString(),
            matches: 0, // Not provided in API
            goals: 0, // Not provided in API
            assists: 0, // Not provided in API
            experience: member.type === 'supervisor' ? 'Staff Member' : null,
            achievements: [],
            role: member.role || {},
            title: member.title || ''
          }));
          setTeamMembers(formattedMembers);
        } else {
          setTeamMembers([]);
        }
      } else {
        // No team found for this player
        setError(`${selectedChild.userName} is not currently part of any team`);
        setTeamData(null);
        setTeamMembers([]);
      }
    } catch (err) {
      console.error('Error fetching team data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load team data');
      setTeamData(null);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const startChat = (member) => {
    // Navigate to chat page with the member
    // For now, navigate to general chat page - this can be enhanced to create/find specific chat room
    navigate(`/chat/${member._id}`);
  };

  const getTeamStats = () => {
    const players = teamMembers.filter(m => m.accountType === 'player');
    const supervisors = teamMembers.filter(m => m.accountType === 'supervisor');
    const onlineMembers = teamMembers.filter(m => m.isOnline).length;
    
    return {
      totalPlayers: players.length,
      totalSupervisors: supervisors.length,
      onlineMembers,
      totalGoals: players.reduce((sum, p) => sum + (p.goals || 0), 0),
      totalAssists: players.reduce((sum, p) => sum + (p.assists || 0), 0)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 mx-auto" style={{borderTopColor: '#e25820', borderBottomColor: '#e25820'}}></div>
          <p className="mt-4 text-gray-600 font-medium">{t('parent.loadingTeamMembers')}</p>
        </div>
      </div>
    );
  }

  const stats = getTeamStats();

  // Handle error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header Section */}
          <TeamHeader selectedChild={selectedChild} teamData={null} stats={stats} />
          
          {/* Error State */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" style={{color: '#e25820'}} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Team Found</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={fetchTeamData}
                className="px-6 py-2 text-white rounded-lg hover:shadow-md transition-all duration-300"
                style={{backgroundColor: '#e25820'}}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d14d1a'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#e25820'}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header Section */}
        <TeamHeader selectedChild={selectedChild} teamData={teamData} stats={stats} />

        {/* Analytics Cards */}
        <TeamAnalytics teamData={teamData} />

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { key: 'overview', label: 'Overview', icon: '📊' },
                { key: 'players', label: 'Players', icon: '⚽' },
                { key: 'staff', label: 'Staff', icon: '👨‍🏫' },
                { key: 'analytics', label: 'Analytics', icon: '📈' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center space-x-2 ${
                    activeTab === tab.key
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  style={activeTab === tab.key ? {borderBottomColor: '#e25820', color: '#e25820'} : {}}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <TeamOverview 
                teamMembers={teamMembers} 
                selectedChild={selectedChild} 
                teamData={teamData}
                startChat={startChat}
              />
            )}

            {/* Players Tab */}
            {activeTab === 'players' && (
              <TeamPlayers 
                teamMembers={teamMembers} 
                startChat={startChat}
              />
            )}

            {/* Staff Tab */}
            {activeTab === 'staff' && (
              <TeamStaff 
                teamMembers={teamMembers} 
                startChat={startChat}
              />
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <TeamAnalyticsTab teamData={teamData} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildControlTeam; 